import { useState } from 'react';
import type { QrCodeKind } from '../api/types';
import { productSearchConfigured } from '../config';
import { DestinationCodeForm } from './DestinationCodeForm';
import { FileCodeForm } from './FileCodeForm';
import { ProductCodeForm } from './ProductCodeForm';
import { KindSwitch, type QrCodeFormProps } from './qrCodeFormParts';

export { isHttpUrl, PRODUCT_COUNTRIES } from './qrCodeFormParts';
export type { QrCodeFormValues, QrCodeFormProps } from './qrCodeFormParts';
export { productCodeTitle, productPageUrl } from './ProductCodeForm';

/**
 * Picks the template for the kind of code being made. The kinds share a group and a set of buttons and
 * almost nothing else - one points wherever somebody types, the other names a product and derives both its
 * title and its destination - so they are separate forms rather than one form full of conditions.
 *
 * Switching kind while creating replaces the form, and with it whatever had been filled in. That is the
 * point: nothing typed for one kind carries a meaning for the other.
 */
export function QrCodeForm(props: QrCodeFormProps) {
  const [kind, setKind] = useState<QrCodeKind>(props.qrCode?.kind ?? 'Destination');

  const isEditing = props.qrCode !== undefined;
  const kinds: QrCodeKind[] = productSearchConfigured() ? ['Destination', 'Product', 'File'] : ['Destination', 'File'];

  const kindSwitch = isEditing ? undefined : (
    <KindSwitch kind={kind} kinds={kinds} disabled={props.submitting ?? false} onChange={setKind} />
  );

  if (kind === 'Product') {
    return <ProductCodeForm {...props} kindSwitch={kindSwitch} />;
  }

  return kind === 'File' ? (
    <FileCodeForm {...props} kindSwitch={kindSwitch} />
  ) : (
    <DestinationCodeForm {...props} kindSwitch={kindSwitch} />
  );
}
